import { track } from "./umami";
/**
 * Client-side reader for the single-source release manifest published at
 * `/downloads/latest.json`. This is the authoritative source for the newest
 * public installers — the download buttons resolve their URLs from here so a
 * new release only requires uploading the installers + updating latest.json.
 *
 * The fetch is best-effort: if the manifest is missing, malformed, or the
 * network fails, callers receive `null` and fall back to the static
 * build-time download URLs in config.ts so the page still works.
 */
import type { DownloadPlatform } from "./config";

/** Public path to the manifest. Same-origin on fileagent.app. */
export const MANIFEST_PATH = "/downloads/latest.json";

export interface ReleaseDownload {
  label: string;
  platform: string;
  arch: string;
  url: string;
  filename: string;
  sha256: string;
  size_bytes: number;
}

export interface ReleaseManifest {
  schema_version?: number;
  product?: string;
  latest_version: string;
  release_date?: string;
  minimum_supported_version?: string;
  release_notes_url?: string;
  downloads: Partial<Record<"linux_deb" | "windows_exe", ReleaseDownload>>;
}

/** Maps the UI platform identifier to the manifest `downloads` key. */
const MANIFEST_KEY: Record<DownloadPlatform, "linux_deb" | "windows_exe"> = {
  linuxDeb: "linux_deb",
  windows: "windows_exe"
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/** Validate a single download entry; returns a clean object or null. */
function normalizeDownload(raw: unknown): ReleaseDownload | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!isNonEmptyString(r.url) || !isNonEmptyString(r.filename)) return null;
  // Only accept absolute HTTPS URLs or same-origin absolute paths.
  const url = r.url.trim();
  if (!/^https:\/\//i.test(url) && !url.startsWith("/")) return null;
  return {
    label: isNonEmptyString(r.label) ? r.label : "",
    platform: isNonEmptyString(r.platform) ? r.platform : "",
    arch: isNonEmptyString(r.arch) ? r.arch : "",
    url,
    filename: r.filename.trim(),
    sha256: isNonEmptyString(r.sha256) ? r.sha256.trim() : "",
    size_bytes: typeof r.size_bytes === "number" && r.size_bytes > 0 ? r.size_bytes : 0
  };
}

/** Validate the top-level manifest shape; returns null when unusable. */
export function normalizeManifest(raw: unknown): ReleaseManifest | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!isNonEmptyString(r.latest_version)) return null;
  const downloadsRaw = (r.downloads && typeof r.downloads === "object" ? r.downloads : {}) as Record<string, unknown>;
  const downloads: ReleaseManifest["downloads"] = {};
  const linux = normalizeDownload(downloadsRaw.linux_deb);
  const windows = normalizeDownload(downloadsRaw.windows_exe);
  if (linux) downloads.linux_deb = linux;
  if (windows) downloads.windows_exe = windows;
  // A manifest with no resolvable downloads is not useful.
  if (!linux && !windows) return null;
  return {
    schema_version: typeof r.schema_version === "number" ? r.schema_version : undefined,
    product: isNonEmptyString(r.product) ? r.product : undefined,
    latest_version: r.latest_version.trim(),
    release_date: isNonEmptyString(r.release_date) ? r.release_date.trim() : undefined,
    minimum_supported_version: isNonEmptyString(r.minimum_supported_version)
      ? r.minimum_supported_version.trim()
      : undefined,
    release_notes_url: isNonEmptyString(r.release_notes_url) ? r.release_notes_url.trim() : undefined,
    downloads
  };
}

/** Fetch + validate the latest-release manifest. Never throws; returns null on any failure. */
export async function fetchLatestRelease(): Promise<ReleaseManifest | null> {
  try {
    const response = await fetch(MANIFEST_PATH, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
      cache: "no-store"
    });
    if (!response.ok) {
      return null;
    }
    const manifest = normalizeManifest(await response.json());
    return manifest;
  } catch {
    track("service_error", { service: "release-manifest", result: "unavailable" });
    return null;
  }
}

/** Return the manifest download entry for a UI platform, if present. */
export function manifestDownloadFor(
  manifest: ReleaseManifest | null | undefined,
  platform: DownloadPlatform
): ReleaseDownload | null {
  if (!manifest) return null;
  return manifest.downloads[MANIFEST_KEY[platform]] ?? null;
}

/** Human-friendly file size, e.g. "44.8 MB". Returns "" when unknown. */
export function formatFileSize(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
}

/** Format an ISO date (YYYY-MM-DD) for display, e.g. "June 1, 2026". Falls back to the raw value. */
export function formatReleaseDate(value: string | undefined): string {
  if (!value) return "";
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
}
